import { ColDef } from "ag-grid-community";
import { RentRollListComponent } from "./rentRollList.component";
import { AgGridColumns, AgGridFilterParams, AgGridDropdownCellEditor } from "src/app/Compliance/AgGrid";
import { AgGridLinkCellRendererComponent, AgGridLinkCellRendererParams } from "src/app/Compliance/AgGrid/CellRenderers/agGridLinkCellRenderer.component";
import { AgGridYesNoFloatingFilterComponent } from "src/app/Compliance/AgGrid/FloatingFilters/agGridYesNoFloatingFilter.component";
import { AgGridMultiSelectedHeaderRenderer, AgGridMultiSelectRendererParams, AgGridMultiSelectedCellRenderer } from "src/app/Compliance/AgGrid/MultiSelectTracker";
import { ICellRendererParamsForRentRollGridAction, RentRollGridActionCellRendererComponent } from "./agGridActionCellRendererComponent";

export const RENT_ROLL_LIST_COLUMNS = (classRef: RentRollListComponent): ColDef[] => [
    {
        colId: 'grid-column-multiselect',
        headerName: '',
        field: 'rentRollLineItemId',
        width: AgGridColumns.selectionColumnWidth,
        suppressSizeToFit: true,
        resizable: false,
        suppressColumnsToolPanel: true,
        pinned: 'left',
        lockPinned: true,
        lockPosition: true,
        editable: false,
        headerComponent: AgGridMultiSelectedHeaderRenderer,
        headerComponentParams: { tracker: classRef.gridTracker } as AgGridMultiSelectRendererParams,
        cellRendererSelector: (params) =>  {
            if (params.data && !params.data.totalsRow) {
                return {
                    component: AgGridMultiSelectedCellRenderer,
                    params: { tracker: classRef.gridTracker } as AgGridMultiSelectRendererParams
                };
            }
        }
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.Unit}`,
        headerName: 'Unit',
        field: 'unit',
        pinned: 'left',
        lockPinned: true,
        lockVisible: true,
        lockPosition: true,
        suppressMovable: true,
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.CompanyName}`,
        headerName: 'Company',
        field: 'companyName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.TopLevelCompanyName}`,
        headerName: 'Top Level Company',
        field: 'topLevelCompanyName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.SiteNumber}`,
        headerName: 'Site Number',
        field: 'siteNumber',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        cellRenderer: AgGridLinkCellRendererComponent,
        cellRendererParams: {
            getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-site',
            newWindow: true,
            getLink: (params: AgGridLinkCellRendererParams) => {
                const lineItem = params.data as Core.RentRollLineItemModel;
                // if (! lineItem && lineItem.sourceSite)) {
                if (!lineItem) {
                    return '';
                }
                return `#/site/${lineItem.siteId}`;
            }
        } as AgGridLinkCellRendererParams
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.IsValidated}`,
        headerName: 'Validated',
        field: 'isValidated',
        type: 'textColumn',
        width: AgGridColumns.textColumnSmallWidth,
        filter: 'agYesNoColumnFilter',
        filterParams: AgGridFilterParams.yesNoFilterParams,
        floatingFilterComponent: AgGridYesNoFloatingFilterComponent,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        valueFormatter: (params) => {
            const data = params.data as Core.RentRollLineItemModel;
            if (!data || !data.rentRollLineItemId) {
                return '-';
            }

            return params.value === true ? 'Yes' : 'No';
        },
        editable: true,
        cellEditor: AgGridDropdownCellEditor,
        cellEditorParams: {
            getOptions: (params) => {
                return [
                    { name: 'Yes', value: true },
                    { name: 'No', value: false }
                ];
            },
            cellFocusLost: (params, updatedValue) => classRef.updateIsValidated(params, updatedValue)
        }
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.SiteName}`,
        headerName: 'Site',
        field: 'siteName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        cellRenderer: AgGridLinkCellRendererComponent,
        cellRendererParams: {
            getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-site',
            newWindow: true,
            getLink: (params: AgGridLinkCellRendererParams) => {
                const lineItem = params.data as Core.RentRollLineItemModel;
                // if (! lineItem && lineItem.sourceSite)) {
                if (!lineItem) {
                    return '';
                }
                return `#/site/${lineItem.siteId}`;
            }
        } as AgGridLinkCellRendererParams
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.UnitType}`,
        headerName: 'Type',
        field: 'unitType',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.Bed}`,
        headerName: 'Bed',
        field: 'bed',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        cellClass: 'ag-numeric-cell',
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.Bath}`,
        headerName: 'Bath',
        field: 'bath',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        cellClass: 'ag-numeric-cell',
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.SqFt}`,
        headerName: 'SqFt',
        field: 'sqFt',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        cellClass: 'ag-numeric-cell'
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.Tenant}`,
        headerName: 'Tenant',
        field: 'tenant',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.RentRollLineItemStatusName}`,
        headerName: 'Status',
        field: 'rentRollLineItemStatusName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.AnnualMarketRent}`,
        headerName: 'Annual Market Rent',
        field: 'annualMarketRent',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: classRef.getCalcOrUncalcValue.bind(classRef),
        cellClass: classRef.getCellClass.bind(classRef)
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.MonthlyMarketRent}`,
        headerName: 'Monthly Market Rent',
        field: 'monthlyMarketRent',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: classRef.getCalcOrUncalcValue.bind(classRef),
        cellClass: classRef.getCellClass.bind(classRef)
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.AnnualRent}`,
        headerName: 'Annual Rent',
        field: 'annualRent',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: classRef.getCalcOrUncalcValue.bind(classRef),
        cellClass: classRef.getCellClass.bind(classRef)
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.MonthlyRent}`,
        headerName: 'Monthly Rent',
        field: 'monthlyRent',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: classRef.getCalcOrUncalcValue.bind(classRef),
        cellClass: classRef.getCellClass.bind(classRef)
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.LeaseBegin}`,
        headerName: 'Lease Begin',
        field: 'leaseBegin',
        width: AgGridColumns.dateColumnWidth,
        filter: 'agDateColumnFilter',
        filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
        valueFormatter: (params) => params.value ? classRef.datePipe.transform(params.value, true) : ''
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.LeaseEnd}`,
        headerName: 'Lease End',
        field: 'leaseEnd',
        width: AgGridColumns.dateColumnWidth,
        filter: 'agDateColumnFilter',
        filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
        valueFormatter: (params) => params.value ? classRef.datePipe.transform(params.value, true) : ''
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.MoveIn}`,
        headerName: 'Move In',
        field: 'moveIn',
        width: AgGridColumns.dateColumnWidth,
        filter: 'agDateColumnFilter',
        filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
        valueFormatter: (params) => params.value ? classRef.datePipe.transform(params.value, true) : '',
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.MoveOut}`,
        headerName: 'Move Out',
        field: 'moveOut',
        width: AgGridColumns.dateColumnWidth,
        filter: 'agDateColumnFilter',
        filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
        valueFormatter: (params) => params.value ? classRef.datePipe.transform(params.value, true) : '',
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.BaseRate}`,
        headerName: 'Base Rate',
        field: 'baseRate',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: (params) => classRef.decimalPipe.transform(params.value, '1.2-2'),
        cellClass: 'ag-numeric-cell',
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.PctOfBuilding}`,
        headerName: 'Pct of Building',
        field: 'pctOfBuilding',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: (params) => classRef.percentPipe.transform(params.value, '1.2-2'),
        cellClass: 'ag-numeric-cell',
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.AnnualOther}`,
        headerName: 'Annual Other',
        field: 'annualOther',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: classRef.getCalcOrUncalcValue.bind(classRef),
        cellClass: classRef.getCellClass.bind(classRef),
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.AnnualCAM}`,
        headerName: 'Annual CAM',
        field: 'annualCAM',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: classRef.getCalcOrUncalcValue.bind(classRef),
        cellClass: classRef.getCellClass.bind(classRef),
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.MonthlyOther}`,
        headerName: 'Monthly Other',
        field: 'monthlyOther',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: classRef.getCalcOrUncalcValue.bind(classRef),
        cellClass: classRef.getCellClass.bind(classRef),
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.MonthlyCAM}`,
        headerName: 'Monthly CAM',
        field: 'monthlyCAM',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: classRef.getCalcOrUncalcValue.bind(classRef),
        cellClass: classRef.getCellClass.bind(classRef),
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.BuildingId}`,
        headerName: 'Building Id',
        field: 'buildingId',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.CreateDate}`,
        headerName: 'Import Date',
        field: 'createDate',
        type: 'dateColumn',
        width: AgGridColumns.dateColumnWidth,
        filter: 'agDateColumnFilter',
        filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
        valueFormatter: (params) => classRef.datePipe.transform(params.value, true),
        cellClass: 'ag-date-cell',
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.ValidatedBy}`,
        headerName: 'Validated By',
        field: 'validatedBy',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        hide: true
    }, {
        colId: `${Core.RentRollLineItemPropertyEnum.ValidatedDate}`,
        headerName: 'Validated Date',
        field: 'validatedDate',
        type: 'dateColumn',
        width: AgGridColumns.dateColumnWidth,
        filter: 'agDateColumnFilter',
        filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
        valueFormatter: (params) => classRef.datePipe.transform(params.value, true),
        cellClass: 'ag-date-cell',
        hide: true
    }, {
        colId: 'grid-column-actions',
        headerName: '',
        field: 'actions',
        width: AgGridColumns.getActionColumnWidth(1),
        minWidth: AgGridColumns.getActionColumnWidth(1),
        maxWidth: AgGridColumns.getActionColumnWidth(1),
        suppressSizeToFit: true,
        suppressAutoSize: true,
        resizable: false,
        suppressColumnsToolPanel: true,
        lockPinned: true,
        pinned: 'right',
        lockVisible: true,
        sortable: false,
        cellRendererSelector: (params) => {
            if (params.data && !params.data.totalsRow) {
                return {
                    component: RentRollGridActionCellRendererComponent,
                    params: {
                        canDelete: (params) => true,
                        delete: classRef.deleteItem.bind(classRef)
                    } as ICellRendererParamsForRentRollGridAction
                };
            }
        }
    }
]
